import apiConfig from 'constants/apiConfig';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import {
  NotificationPriority,
  NotificationWithMetadataDto,
} from './useNotification';
import { toast } from 'react-toastify';
import { useSseStream } from './useSseStream';

type NotificationStreamEvent = {
  type: string;
  notification: NotificationWithMetadataDto;
};

export const useNotificationStream = () => {
  const [notifications] = useState([]);
  const queryClient = useQueryClient();
  const groupGuid = useAppSelector(selectGroupGuid);

  const handleEvent = (data: NotificationStreamEvent) => {
    if (data.type === 'new_notification') {
      queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });

      queryClient.invalidateQueries({
        queryKey: ['notificationsByGroup', groupGuid],
      });

      if (data.notification.priority === NotificationPriority.CRITICAL) {
        toast.error(
          `STAND DOWN SAFETY ALERT RECEIVED: ${data.notification.metadata.appMessage || data.notification.metadata.emailMessage}`
        );
      }
    }
  };

  const { isConnected } = useSseStream<NotificationStreamEvent>({
    url: apiConfig.apiGateway.notificationStream,
    onEvent: handleEvent,
    getToken: () => sessionStorage['accessToken'] ?? null,
    // Note: the axios interceptor also considers auth.teamsInitialized.
    getIsTeams: () => sessionStorage['isUsingTeams'] === 'true',
    logPrefix: '[SSE]',
    maxReconnectAttempts: 3,
    reconnectDelayMs: (attempt) => attempt * 5000, // 5s, 10s, 15s
    reconnectLogLevel: 'log',
    deps: [sessionStorage['accessToken']],
  });

  return { notifications, isConnected };
};
